/**
 * TradingView-style color picker. The popover shows:
 *   1. A 10×7 preset grid: row 0 grayscale, row 1 vivid hues, rows 2–6
 *      progressive tint→dark variants of the same hue column.
 *   2. A "+" button that opens the native picker for arbitrary hex.
 *   3. An opacity slider (0–100 %) that controls the line's alpha.
 *
 * The popover renders through a React portal into document.body so it
 * escapes the dialog's `overflow-y: auto`. Position is computed from
 * the trigger's viewport coordinates (`position: fixed`) and
 * automatically flips above when there's not enough room below.
 *
 * Closes on outside click or Esc.
 */
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styles from "./ColorPicker.module.css";

const PALETTE: readonly string[] = [
  // Pulsarix theme palette — design-system surfaces + brand accents.
  // (#090D16 is the default chart background.)
  "#06080F", "#090D16", "#0C111C", "#111827", "#161F30", "#3D7BFF", "#23D5A4", "#FF5B6B", "#F4C44C", "#E8EDF7",
  "#FFFFFF", "#ECEFF1", "#CFD8DC", "#B0BEC5", "#90A4AE", "#78909C", "#546E7A", "#37474F", "#263238", "#000000",
  "#F44336", "#FF9800", "#FFEB3B", "#4CAF50", "#009688", "#00BCD4", "#2196F3", "#3F51B5", "#9C27B0", "#E91E63",
  "#FFCDD2", "#FFE0B2", "#FFF9C4", "#C8E6C9", "#B2DFDB", "#B2EBF2", "#BBDEFB", "#C5CAE9", "#E1BEE7", "#F8BBD0",
  "#EF9A9A", "#FFCC80", "#FFF59D", "#A5D6A7", "#80CBC4", "#80DEEA", "#90CAF9", "#9FA8DA", "#CE93D8", "#F48FB1",
  "#E57373", "#FFB74D", "#FFF176", "#81C784", "#4DB6AC", "#4DD0E1", "#64B5F6", "#7986CB", "#BA68C8", "#F06292",
  "#D32F2F", "#F57C00", "#FBC02D", "#388E3C", "#00796B", "#0097A7", "#1976D2", "#303F9F", "#7B1FA2", "#C2185B",
  "#B71C1C", "#E65100", "#F57F17", "#1B5E20", "#004D40", "#006064", "#0D47A1", "#1A237E", "#4A148C", "#880E4F",
];

// Approximate; only used to decide above vs below before measuring
// the rendered popover. The portal layer measures and corrects itself
// in a follow-up effect once content is on screen.
const POPOVER_HEIGHT_PX = 280;
const POPOVER_WIDTH_PX = 260;
// Padding between trigger and popover.
const GAP_PX = 6;
// Margin between popover edge and viewport edge.
const VIEWPORT_MARGIN_PX = 8;

interface Coords {
  top: number;
  left: number;
}

interface Props {
  value: string;
  /** 0..1; defaults to 1 if undefined. */
  opacity?: number;
  onChange: (color: string, opacity: number) => void;
  ariaLabel?: string;
}

function normalize(hex: string): string {
  return hex.trim().toLowerCase();
}

export function ColorPicker({ value, opacity = 1, onChange, ariaLabel }: Props) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<Coords | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const customInputRef = useRef<HTMLInputElement>(null);

  // First measurement: positions popover based on trigger rect alone.
  // Runs before paint so there's no flash at the wrong spot.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current) {
      setCoords(null);
      return;
    }
    const rect = wrapperRef.current.getBoundingClientRect();
    setCoords(computePlacement(rect, POPOVER_HEIGHT_PX, POPOVER_WIDTH_PX));
  }, [open]);

  // Second pass after the popover has been rendered into the portal:
  // adjust using its real size in case the estimate was off. This
  // catches cases where the estimated height was larger than reality
  // and we'd otherwise float above unnecessarily.
  useLayoutEffect(() => {
    if (!open || !wrapperRef.current || !popoverRef.current) return;
    const triggerRect = wrapperRef.current.getBoundingClientRect();
    const popRect = popoverRef.current.getBoundingClientRect();
    setCoords(
      computePlacement(triggerRect, popRect.height, popRect.width),
    );
    // Re-run only when coords first become non-null; subsequent
    // size changes during open are vanishingly rare.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, coords !== null]);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !wrapperRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = normalize(value);
  const opacityPct = Math.round(opacity * 100);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <button
        type="button"
        className={styles.swatch}
        style={{ backgroundColor: value, opacity }}
        onClick={() => setOpen((v) => !v)}
        aria-label={ariaLabel ?? "Pick color"}
        aria-haspopup="dialog"
        aria-expanded={open}
      />
      {open && coords
        ? createPortal(
            <div
              ref={popoverRef}
              className={styles.popover}
              role="dialog"
              style={{
                position: "fixed",
                top: coords.top,
                left: coords.left,
              }}
            >
              <div className={styles.grid}>
                {PALETTE.map((c) => {
                  const selected = normalize(c) === current;
                  return (
                    <button
                      key={c}
                      type="button"
                      className={`${styles.preset} ${selected ? styles.selected : ""}`}
                      style={{ backgroundColor: c }}
                      // Keep the popover open after picking a color so the
                      // user can also tweak opacity in the same session.
                      // Closes on click-outside / Esc.
                      onClick={() => onChange(c, opacity)}
                      aria-label={c}
                      title={c}
                    />
                  );
                })}
              </div>

              <div className={styles.customRow}>
                <button
                  type="button"
                  className={styles.customBtn}
                  onClick={() => customInputRef.current?.click()}
                  title="Custom color"
                  aria-label="Custom color"
                >
                  +
                </button>
                <input
                  ref={customInputRef}
                  type="color"
                  value={value}
                  onChange={(e) => onChange(e.target.value, opacity)}
                  className={styles.customInput}
                  aria-hidden
                />
              </div>

              <div className={styles.opacityRow}>
                <label className={styles.opacityLabel} htmlFor="cp-opacity">
                  Opacity
                </label>
                <input
                  id="cp-opacity"
                  type="range"
                  min={0}
                  max={100}
                  step={1}
                  value={opacityPct}
                  onChange={(e) =>
                    onChange(value, Number(e.target.value) / 100)
                  }
                  className={styles.opacityInput}
                  style={{
                    background: `linear-gradient(to right, transparent, ${value})`,
                  }}
                />
                <span className={styles.opacityValue}>{opacityPct}%</span>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** Decide where to place the popover given the trigger rect and the
 * popover's (estimated or measured) size. Flips above if below is too
 * tight; clamps inside the viewport horizontally. */
function computePlacement(
  trigger: DOMRect,
  height: number,
  width: number,
): Coords {
  const spaceBelow = window.innerHeight - trigger.bottom;
  const spaceAbove = trigger.top;

  // Vertical: prefer below; flip above if not enough room and above
  // has more space.
  const placeAbove =
    spaceBelow < height + GAP_PX && spaceAbove > spaceBelow;
  const top = placeAbove
    ? Math.max(VIEWPORT_MARGIN_PX, trigger.top - height - GAP_PX)
    : trigger.bottom + GAP_PX;

  // Horizontal: align popover's right edge with trigger's right edge.
  // If that pushes off-screen left, snap to the viewport margin.
  let left = trigger.right - width;
  if (left < VIEWPORT_MARGIN_PX) left = VIEWPORT_MARGIN_PX;
  if (left + width > window.innerWidth - VIEWPORT_MARGIN_PX) {
    left = window.innerWidth - width - VIEWPORT_MARGIN_PX;
  }

  return { top, left };
}
